/**
 * Pure presentation helpers for the sensor Quality Control feature.
 *
 * Statuses and the pass/fail rule are computed by the backend - nothing here re-derives them,
 * it only maps a status onto a label and a cell highlight. Kept free of any React/MUI/`src/api`
 * import so it stays unit-testable through jest.
 */

export type QualityControlSeverity = 'error' | 'warning' | 'none'

/** Rendered for a check with no value: a `failed` check, or one the backend did not report. */
export const EMPTY_VALUE = '—'

/**
 * The value behind the check is unknown or stale, so the check could not be evaluated:
 * measurements / battery / signalStrength past their 24h validity window, activation with no
 * ThingSpace record, configuration with no config row. Always arrives as `value: null`.
 */
export const FAILED_STATUS = 'failed'

/** Statuses that mean the check failed - the cell is highlighted red. */
const ERROR_STATUSES = ['deactivated', 'issues', 'weak', 'low', FAILED_STATUS]

/** Statuses that are a warning but still count as passed - the cell is highlighted yellow. */
const WARNING_STATUSES = ['fair', 'warning']

export const getQualityControlSeverity = (status?: string | null): QualityControlSeverity => {
  if (!status) return 'none'
  if (ERROR_STATUSES.includes(status)) return 'error'
  if (WARNING_STATUSES.includes(status)) return 'warning'
  return 'none'
}

const STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  deactivated: 'Deactivated',
  ok: 'Ok',
  issues: 'Issues',
  good: 'Good',
  fair: 'Fair',
  weak: 'Weak',
  warning: 'Warning',
  low: 'Low'
}

/**
 * Activation, Configuration, Measurements and Signal strength all display their status.
 * A `failed` check has no value to describe, so it renders as a placeholder (still highlighted red).
 */
export const formatQualityControlStatus = (status?: string | null): string => {
  if (!status || status === FAILED_STATUS) return EMPTY_VALUE
  return STATUS_LABELS[status] || status
}

/**
 * Battery displays the numeric level instead of its status. A stale or unknown level arrives as
 * `value: null` (with status `failed`) and renders as a placeholder - never as 0%, which is a
 * legitimate reading in its own right.
 */
export const formatBatteryLevel = (value?: number | null): string => {
  if (typeof value === 'number') return `${value}%`
  return EMPTY_VALUE
}

export const formatChecksPassed = (passedChecks?: number | null, totalChecks?: number | null): string => {
  if (passedChecks === null || passedChecks === undefined || !totalChecks) return EMPTY_VALUE
  return `${passedChecks} of ${totalChecks} checks passed`
}

export const formatQualityControlSummary = (summary?: { checked?: number; passed?: number; failed?: number } | null): string => {
  const checked = summary?.checked || 0
  const passed = summary?.passed || 0
  const failed = summary?.failed || 0
  return `${checked} ${checked === 1 ? 'device' : 'devices'} checked (${passed} passed / ${failed} failed)`
}
